// views/Settings/MinimumOrderValueSettings.js
// The platform-wide order floor (services/minimumOrderValueService.js on the
// server): merchandise subtotal, per vendor, a hard block. Had an API since
// 20 Sep 2026 and no screen until the Settings menu.
import React, { useEffect, useState } from "react";
import { notify } from "../../utilities/notify";
import SettingsPanel from "../../components/SettingsPanel";
import { THEME as C } from "../../components/dashboardTheme";
import { apiGetMinimumOrderValue, apiUpdateMinimumOrderValue } from "../../api";
import { field, LastChanged, ReadOnlyNotice, SaveButton } from "./settingsUi";
import SettingHelp from "./settingsHelp";
import { AppButton } from "../../components/ui";

const MARKETS = [
  { code: "UG", label: "Uganda" },
  { code: "ZM", label: "Zambia" },
];

const MinimumOrderValueSettings = ({ canEdit = true }) => {
  const [country, setCountry] = useState("UG");
  const [policy, setPolicy] = useState(null);
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(0);

  const load = async (cc) => {
    setLoading(true);
    try {
      const { data } = await apiGetMinimumOrderValue(cc);
      setPolicy(data);
      setValue(String(data?.minimumOrderValue ?? ""));
    } catch (err) {
      notify.fromError(err, "Could not load the minimum order value");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(country); }, [country]);

  const n = Number(value);
  const valid = value !== "" && Number.isFinite(n) && n >= 0;
  const unchanged = policy && valid && Math.floor(n) === Number(policy.minimumOrderValue);

  const save = async (e) => {
    e.preventDefault();
    if (!valid) return notify.error("Enter 0 or a positive amount", "0 switches the rule off for this market.");
    setSaving(true);
    try {
      const { data } = await apiUpdateMinimumOrderValue(Math.floor(n), country);
      setPolicy((p) => ({ ...p, ...data }));
      setSavedAt(Date.now());
      notify.success(
        data.enabled ? `Minimum order value is now ${data.currency} ${Number(data.minimumOrderValue).toLocaleString()}` : "Minimum order value switched off",
        `${MARKETS.find((m) => m.code === country)?.label || country}. Every checkout reads it within about 30 seconds.`,
      );
    } catch (err) {
      notify.fromError(err, "Could not save the minimum order value");
    } finally {
      setSaving(false);
    }
  };

  const currency = policy?.currency || (country === "ZM" ? "ZMW" : "UGX");

  return (
    <SettingsPanel
      icon="fa-basket-shopping"
      title="Minimum order value"
      subtitle={loading ? "Loading..." : policy?.enabled
        ? `${currency} ${Number(policy.minimumOrderValue).toLocaleString()} of goods per vendor, delivery fee excluded`
        : "Switched off for this market"}
    >
      {!canEdit && <ReadOnlyNotice level="manage" />}
      <form onSubmit={save}>
        <SettingsPanel.Section title="What this does" icon="fa-circle-info">
          <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: C.textMid }}>
            A delivered order must carry at least this much in goods from each vendor before it can be placed;
            the server refuses a smaller basket and tells the customer how much to add. The delivery fee and the
            payment fee do not count towards it. Below the floor the commission does not cover picking,
            dispatching a rider and the payment fee, so the order is refused rather than run at a loss.
            Walk-in sales, tickets, parcels and accepted brand quotes are exempt. A brand can set a higher
            floor of its own, and the higher of the two applies.
            <SettingHelp topic="vendorMinimumOrderValue" />
          </p>
        </SettingsPanel.Section>

        <SettingsPanel.Section title="Floor for each market" icon="fa-sliders-h">
          <div style={field.grid(2)}>
            <div>
              <label style={field.label}>Market<SettingHelp topic="minimumOrderValueMarket" /></label>
              <select style={field.input} value={country} onChange={(e) => setCountry(e.target.value)} disabled={saving}>
                {MARKETS.map((m) => <option key={m.code} value={m.code}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <label style={field.label}>
                Smallest goods value per vendor ({currency})
                <SettingHelp topic="minimumOrderValue" />
              </label>
              <input
                style={field.input}
                type="number" min="0" step="500" inputMode="numeric"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                disabled={!canEdit || saving || loading}
              />
              <div style={field.help}>
                Goods only, counted per vendor; the delivery fee does not count. Server default {currency} {Number(policy?.default ?? 0).toLocaleString()}. Enter 0 to switch the rule off in this market.
              </div>
            </div>
          </div>
          <div style={field.actions}>
            {canEdit && <SaveButton saving={saving} disabled={!valid || unchanged || loading} />}
            {policy && policy.default != null && Number(policy.minimumOrderValue) !== Number(policy.default) && canEdit && (
              <AppButton variant="quiet" icon="fas fa-rotate-left" onClick={() => setValue(String(policy.default))} disabled={saving}>
                Use the server default
              </AppButton>
            )}
            <div style={{ marginLeft: "auto" }}>
              <LastChanged keys={[`minimum_order_value_${country}`]} refreshToken={savedAt} />
            </div>
          </div>
        </SettingsPanel.Section>
      </form>
    </SettingsPanel>
  );
};

export default MinimumOrderValueSettings;
